/**
 * Helper to create CSS token variables from the merged-tokens for light and dark modes.
 * To be use when token color values need to be displayed in the context of their colorMode and independent
 * of the displayed theme.
 */

// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import tokenNameCssMap from '@atlaskit/tokens/token-names';

import mergedTokens from './merged-tokens';
import type { TransformedTokenMerged } from './types';

const tokenNames = Object.keys(tokenNameCssMap);
type TokenNames = keyof typeof tokenNameCssMap;
type ColorModeTokenMerged = {
	cleanName: TokenNames;
	value: string;
	darkToken: { value: string };
} & TransformedTokenMerged;

export const tokenColorModeValues: {
	'color.text': {
		light: string;
		dark: string;
	};
	'color.text.accent.lime': {
		light: string;
		dark: string;
	};
	'color.text.accent.lime.bolder': {
		light: string;
		dark: string;
	};
	'color.text.accent.red': {
		light: string;
		dark: string;
	};
	'color.text.accent.red.bolder': {
		light: string;
		dark: string;
	};
	'color.text.accent.orange': {
		light: string;
		dark: string;
	};
	'color.text.accent.orange.bolder': {
		light: string;
		dark: string;
	};
	'color.text.accent.yellow': {
		light: string;
		dark: string;
	};
	'color.text.accent.yellow.bolder': {
		light: string;
		dark: string;
	};
	'color.text.accent.green': {
		light: string;
		dark: string;
	};
	'color.text.accent.green.bolder': {
		light: string;
		dark: string;
	};
	'color.text.accent.teal': {
		light: string;
		dark: string;
	};
	'color.text.accent.teal.bolder': {
		light: string;
		dark: string;
	};
	'color.text.accent.blue': {
		light: string;
		dark: string;
	};
	'color.text.accent.blue.bolder': {
		light: string;
		dark: string;
	};
	'color.text.accent.purple': {
		light: string;
		dark: string;
	};
	'color.text.accent.purple.bolder': {
		light: string;
		dark: string;
	};
	'color.text.accent.magenta': {
		light: string;
		dark: string;
	};
	'color.text.accent.magenta.bolder': {
		light: string;
		dark: string;
	};
	'color.text.accent.gray': {
		light: string;
		dark: string;
	};
	'color.text.accent.gray.bolder': {
		light: string;
		dark: string;
	};
	'color.text.disabled': {
		light: string;
		dark: string;
	};
	'color.text.inverse': {
		light: string;
		dark: string;
	};
	'color.text.selected': {
		light: string;
		dark: string;
	};
	'color.text.brand': {
		light: string;
		dark: string;
	};
	'color.text.danger': {
		light: string;
		dark: string;
	};
	'color.text.danger.bolder': {
		light: string;
		dark: string;
	};
	'color.text.warning': {
		light: string;
		dark: string;
	};
	'color.text.warning.inverse': {
		light: string;
		dark: string;
	};
	'color.text.warning.bolder': {
		light: string;
		dark: string;
	};
	'color.text.success': {
		light: string;
		dark: string;
	};
	'color.text.success.bolder': {
		light: string;
		dark: string;
	};
	'color.text.discovery': {
		light: string;
		dark: string;
	};
	'color.text.discovery.bolder': {
		light: string;
		dark: string;
	};
	'color.text.information': {
		light: string;
		dark: string;
	};
	'color.text.information.bolder': {
		light: string;
		dark: string;
	};
	'color.text.subtlest': {
		light: string;
		dark: string;
	};
	'color.text.subtle': {
		light: string;
		dark: string;
	};
	'color.link': {
		light: string;
		dark: string;
	};
	'color.link.pressed': {
		light: string;
		dark: string;
	};
	'color.link.visited': {
		light: string;
		dark: string;
	};
	'color.link.visited.pressed': {
		light: string;
		dark: string;
	};
	'color.icon': {
		light: string;
		dark: string;
	};
	'color.icon.accent.lime': {
		light: string;
		dark: string;
	};
	'color.icon.accent.red': {
		light: string;
		dark: string;
	};
	'color.icon.accent.orange': {
		light: string;
		dark: string;
	};
	'color.icon.accent.yellow': {
		light: string;
		dark: string;
	};
	'color.icon.accent.green': {
		light: string;
		dark: string;
	};
	'color.icon.accent.teal': {
		light: string;
		dark: string;
	};
	'color.icon.accent.blue': {
		light: string;
		dark: string;
	};
	'color.icon.accent.purple': {
		light: string;
		dark: string;
	};
	'color.icon.accent.magenta': {
		light: string;
		dark: string;
	};
	'color.icon.accent.gray': {
		light: string;
		dark: string;
	};
	'color.icon.disabled': {
		light: string;
		dark: string;
	};
	'color.icon.inverse': {
		light: string;
		dark: string;
	};
	'color.icon.selected': {
		light: string;
		dark: string;
	};
	'color.icon.brand': {
		light: string;
		dark: string;
	};
	'color.icon.danger': {
		light: string;
		dark: string;
	};
	'color.icon.warning': {
		light: string;
		dark: string;
	};
	'color.icon.warning.inverse': {
		light: string;
		dark: string;
	};
	'color.icon.success': {
		light: string;
		dark: string;
	};
	'color.icon.discovery': {
		light: string;
		dark: string;
	};
	'color.icon.information': {
		light: string;
		dark: string;
	};
	'color.icon.subtlest': {
		light: string;
		dark: string;
	};
	'color.icon.subtle': {
		light: string;
		dark: string;
	};
	'color.border': {
		light: string;
		dark: string;
	};
	'color.border.accent.lime': {
		light: string;
		dark: string;
	};
	'color.border.accent.red': {
		light: string;
		dark: string;
	};
	'color.border.accent.orange': {
		light: string;
		dark: string;
	};
	'color.border.accent.yellow': {
		light: string;
		dark: string;
	};
	'color.border.accent.green': {
		light: string;
		dark: string;
	};
	'color.border.accent.teal': {
		light: string;
		dark: string;
	};
	'color.border.accent.blue': {
		light: string;
		dark: string;
	};
	'color.border.accent.purple': {
		light: string;
		dark: string;
	};
	'color.border.accent.magenta': {
		light: string;
		dark: string;
	};
	'color.border.accent.gray': {
		light: string;
		dark: string;
	};
	'color.border.disabled': {
		light: string;
		dark: string;
	};
	'color.border.focused': {
		light: string;
		dark: string;
	};
	'color.border.input': {
		light: string;
		dark: string;
	};
	'color.border.inverse': {
		light: string;
		dark: string;
	};
	'color.border.selected': {
		light: string;
		dark: string;
	};
	'color.border.brand': {
		light: string;
		dark: string;
	};
	'color.border.danger': {
		light: string;
		dark: string;
	};
	'color.border.warning': {
		light: string;
		dark: string;
	};
	'color.border.success': {
		light: string;
		dark: string;
	};
	'color.border.discovery': {
		light: string;
		dark: string;
	};
	'color.border.information': {
		light: string;
		dark: string;
	};
	'color.border.bold': {
		light: string;
		dark: string;
	};
	'color.background.accent.lime.subtlest': {
		light: string;
		dark: string;
	};
	'color.background.accent.lime.subtlest.hovered': {
		light: string;
		dark: string;
	};
	'color.background.accent.lime.subtlest.pressed': {
		light: string;
		dark: string;
	};
	'color.background.accent.lime.subtler': {
		light: string;
		dark: string;
	};
	'color.background.accent.lime.subtler.hovered': {
		light: string;
		dark: string;
	};
	'color.background.accent.lime.subtler.pressed': {
		light: string;
		dark: string;
	};
	'color.background.accent.lime.subtle': {
		light: string;
		dark: string;
	};
	'color.background.accent.lime.subtle.hovered': {
		light: string;
		dark: string;
	};
	'color.background.accent.lime.subtle.pressed': {
		light: string;
		dark: string;
	};
	'color.background.accent.lime.bolder': {
		light: string;
		dark: string;
	};
	'color.background.accent.lime.bolder.hovered': {
		light: string;
		dark: string;
	};
	'color.background.accent.lime.bolder.pressed': {
		light: string;
		dark: string;
	};
	'color.background.accent.red.subtlest': {
		light: string;
		dark: string;
	};
	'color.background.accent.red.subtlest.hovered': {
		light: string;
		dark: string;
	};
	'color.background.accent.red.subtlest.pressed': {
		light: string;
		dark: string;
	};
	'color.background.accent.red.subtler': {
		light: string;
		dark: string;
	};
	'color.background.accent.red.subtler.hovered': {
		light: string;
		dark: string;
	};
	'color.background.accent.red.subtler.pressed': {
		light: string;
		dark: string;
	};
	'color.background.accent.red.subtle': {
		light: string;
		dark: string;
	};
	'color.background.accent.red.subtle.hovered': {
		light: string;
		dark: string;
	};
	'color.background.accent.red.subtle.pressed': {
		light: string;
		dark: string;
	};
	'color.background.accent.red.bolder': {
		light: string;
		dark: string;
	};
	'color.background.accent.red.bolder.hovered': {
		light: string;
		dark: string;
	};
	'color.background.accent.red.bolder.pressed': {
		light: string;
		dark: string;
	};
	'color.background.accent.orange.subtlest': {
		light: string;
		dark: string;
	};
	'color.background.accent.orange.subtlest.hovered': {
		light: string;
		dark: string;
	};
	'color.background.accent.orange.subtlest.pressed': {
		light: string;
		dark: string;
	};
	'color.background.accent.orange.subtler': {
		light: string;
		dark: string;
	};
	'color.background.accent.orange.subtler.hovered': {
		light: string;
		dark: string;
	};
	'color.background.accent.orange.subtler.pressed': {
		light: string;
		dark: string;
	};
	'color.background.accent.orange.subtle': {
		light: string;
		dark: string;
	};
	'color.background.accent.orange.subtle.hovered': {
		light: string;
		dark: string;
	};
	'color.background.accent.orange.subtle.pressed': {
		light: string;
		dark: string;
	};
	'color.background.accent.orange.bolder': {
		light: string;
		dark: string;
	};
	'color.background.accent.orange.bolder.hovered': {
		light: string;
		dark: string;
	};
	'color.background.accent.orange.bolder.pressed': {
		light: string;
		dark: string;
	};
	'color.background.accent.yellow.subtlest': {
		light: string;
		dark: string;
	};
	'color.background.accent.yellow.subtlest.hovered': {
		light: string;
		dark: string;
	};
	'color.background.accent.yellow.subtlest.pressed': {
		light: string;
		dark: string;
	};
	'color.background.accent.yellow.subtler': {
		light: string;
		dark: string;
	};
	'color.background.accent.yellow.subtler.hovered': {
		light: string;
		dark: string;
	};
	'color.background.accent.yellow.subtler.pressed': {
		light: string;
		dark: string;
	};
	'color.background.accent.yellow.subtle': {
		light: string;
		dark: string;
	};
	'color.background.accent.yellow.subtle.hovered': {
		light: string;
		dark: string;
	};
	'color.background.accent.yellow.subtle.pressed': {
		light: string;
		dark: string;
	};
	'color.background.accent.yellow.bolder': {
		light: string;
		dark: string;
	};
	'color.background.accent.yellow.bolder.hovered': {
		light: string;
		dark: string;
	};
	'color.background.accent.yellow.bolder.pressed': {
		light: string;
		dark: string;
	};
	'color.background.accent.green.subtlest': {
		light: string;
		dark: string;
	};
	'color.background.accent.green.subtlest.hovered': {
		light: string;
		dark: string;
	};
	'color.background.accent.green.subtlest.pressed': {
		light: string;
		dark: string;
	};
	'color.background.accent.green.subtler': {
		light: string;
		dark: string;
	};
	'color.background.accent.green.subtler.hovered': {
		light: string;
		dark: string;
	};
	'color.background.accent.green.subtler.pressed': {
		light: string;
		dark: string;
	};
	'color.background.accent.green.subtle': {
		light: string;
		dark: string;
	};
	'color.background.accent.green.subtle.hovered': {
		light: string;
		dark: string;
	};
	'color.background.accent.green.subtle.pressed': {
		light: string;
		dark: string;
	};
	'color.background.accent.green.bolder': {
		light: string;
		dark: string;
	};
	'color.background.accent.green.bolder.hovered': {
		light: string;
		dark: string;
	};
	'color.background.accent.green.bolder.pressed': {
		light: string;
		dark: string;
	};
	'color.background.accent.teal.subtlest': {
		light: string;
		dark: string;
	};
	'color.background.accent.teal.subtlest.hovered': {
		light: string;
		dark: string;
	};
	'color.background.accent.teal.subtlest.pressed': {
		light: string;
		dark: string;
	};
	'color.background.accent.teal.subtler': {
		light: string;
		dark: string;
	};
	'color.background.accent.teal.subtler.hovered': {
		light: string;
		dark: string;
	};
	'color.background.accent.teal.subtler.pressed': {
		light: string;
		dark: string;
	};
	'color.background.accent.teal.subtle': {
		light: string;
		dark: string;
	};
	'color.background.accent.teal.subtle.hovered': {
		light: string;
		dark: string;
	};
	'color.background.accent.teal.subtle.pressed': {
		light: string;
		dark: string;
	};
	'color.background.accent.teal.bolder': {
		light: string;
		dark: string;
	};
	'color.background.accent.teal.bolder.hovered': {
		light: string;
		dark: string;
	};
	'color.background.accent.teal.bolder.pressed': {
		light: string;
		dark: string;
	};
	'color.background.accent.blue.subtlest': {
		light: string;
		dark: string;
	};
	'color.background.accent.blue.subtlest.hovered': {
		light: string;
		dark: string;
	};
	'color.background.accent.blue.subtlest.pressed': {
		light: string;
		dark: string;
	};
	'color.background.accent.blue.subtler': {
		light: string;
		dark: string;
	};
	'color.background.accent.blue.subtler.hovered': {
		light: string;
		dark: string;
	};
	'color.background.accent.blue.subtler.pressed': {
		light: string;
		dark: string;
	};
	'color.background.accent.blue.subtle': {
		light: string;
		dark: string;
	};
	'color.background.accent.blue.subtle.hovered': {
		light: string;
		dark: string;
	};
	'color.background.accent.blue.subtle.pressed': {
		light: string;
		dark: string;
	};
	'color.background.accent.blue.bolder': {
		light: string;
		dark: string;
	};
	'color.background.accent.blue.bolder.hovered': {
		light: string;
		dark: string;
	};
	'color.background.accent.blue.bolder.pressed': {
		light: string;
		dark: string;
	};
	'color.background.accent.purple.subtlest': {
		light: string;
		dark: string;
	};
	'color.background.accent.purple.subtlest.hovered': {
		light: string;
		dark: string;
	};
	'color.background.accent.purple.subtlest.pressed': {
		light: string;
		dark: string;
	};
	'color.background.accent.purple.subtler': {
		light: string;
		dark: string;
	};
	'color.background.accent.purple.subtler.hovered': {
		light: string;
		dark: string;
	};
	'color.background.accent.purple.subtler.pressed': {
		light: string;
		dark: string;
	};
	'color.background.accent.purple.subtle': {
		light: string;
		dark: string;
	};
	'color.background.accent.purple.subtle.hovered': {
		light: string;
		dark: string;
	};
	'color.background.accent.purple.subtle.pressed': {
		light: string;
		dark: string;
	};
	'color.background.accent.purple.bolder': {
		light: string;
		dark: string;
	};
	'color.background.accent.purple.bolder.hovered': {
		light: string;
		dark: string;
	};
	'color.background.accent.purple.bolder.pressed': {
		light: string;
		dark: string;
	};
	'color.background.accent.magenta.subtlest': {
		light: string;
		dark: string;
	};
	'color.background.accent.magenta.subtlest.hovered': {
		light: string;
		dark: string;
	};
	'color.background.accent.magenta.subtlest.pressed': {
		light: string;
		dark: string;
	};
	'color.background.accent.magenta.subtler': {
		light: string;
		dark: string;
	};
	'color.background.accent.magenta.subtler.hovered': {
		light: string;
		dark: string;
	};
	'color.background.accent.magenta.subtler.pressed': {
		light: string;
		dark: string;
	};
	'color.background.accent.magenta.subtle': {
		light: string;
		dark: string;
	};
	'color.background.accent.magenta.subtle.hovered': {
		light: string;
		dark: string;
	};
	'color.background.accent.magenta.subtle.pressed': {
		light: string;
		dark: string;
	};
	'color.background.accent.magenta.bolder': {
		light: string;
		dark: string;
	};
	'color.background.accent.magenta.bolder.hovered': {
		light: string;
		dark: string;
	};
	'color.background.accent.magenta.bolder.pressed': {
		light: string;
		dark: string;
	};
	'color.background.accent.gray.subtlest': {
		light: string;
		dark: string;
	};
	'color.background.accent.gray.subtlest.hovered': {
		light: string;
		dark: string;
	};
	'color.background.accent.gray.subtlest.pressed': {
		light: string;
		dark: string;
	};
	'color.background.accent.gray.subtler': {
		light: string;
		dark: string;
	};
	'color.background.accent.gray.subtler.hovered': {
		light: string;
		dark: string;
	};
	'color.background.accent.gray.subtler.pressed': {
		light: string;
		dark: string;
	};
	'color.background.accent.gray.subtle': {
		light: string;
		dark: string;
	};
	'color.background.accent.gray.subtle.hovered': {
		light: string;
		dark: string;
	};
	'color.background.accent.gray.subtle.pressed': {
		light: string;
		dark: string;
	};
	'color.background.accent.gray.bolder': {
		light: string;
		dark: string;
	};
	'color.background.accent.gray.bolder.hovered': {
		light: string;
		dark: string;
	};
	'color.background.accent.gray.bolder.pressed': {
		light: string;
		dark: string;
	};
	'color.background.disabled': {
		light: string;
		dark: string;
	};
	'color.background.input': {
		light: string;
		dark: string;
	};
	'color.background.input.hovered': {
		light: string;
		dark: string;
	};
	'color.background.input.pressed': {
		light: string;
		dark: string;
	};
	'color.background.inverse.subtle': {
		light: string;
		dark: string;
	};
	'color.background.inverse.subtle.hovered': {
		light: string;
		dark: string;
	};
	'color.background.inverse.subtle.pressed': {
		light: string;
		dark: string;
	};
	'color.background.neutral': {
		light: string;
		dark: string;
	};
	'color.background.neutral.hovered': {
		light: string;
		dark: string;
	};
	'color.background.neutral.pressed': {
		light: string;
		dark: string;
	};
	'color.background.neutral.subtle': {
		light: string;
		dark: string;
	};
	'color.background.neutral.subtle.hovered': {
		light: string;
		dark: string;
	};
	'color.background.neutral.subtle.pressed': {
		light: string;
		dark: string;
	};
	'color.background.neutral.bold': {
		light: string;
		dark: string;
	};
	'color.background.neutral.bold.hovered': {
		light: string;
		dark: string;
	};
	'color.background.neutral.bold.pressed': {
		light: string;
		dark: string;
	};
	'color.background.selected': {
		light: string;
		dark: string;
	};
	'color.background.selected.hovered': {
		light: string;
		dark: string;
	};
	'color.background.selected.pressed': {
		light: string;
		dark: string;
	};
	'color.background.selected.bold': {
		light: string;
		dark: string;
	};
	'color.background.selected.bold.hovered': {
		light: string;
		dark: string;
	};
	'color.background.selected.bold.pressed': {
		light: string;
		dark: string;
	};
	'color.background.brand.subtlest': {
		light: string;
		dark: string;
	};
	'color.background.brand.subtlest.hovered': {
		light: string;
		dark: string;
	};
	'color.background.brand.subtlest.pressed': {
		light: string;
		dark: string;
	};
	'color.background.brand.bold': {
		light: string;
		dark: string;
	};
	'color.background.brand.bold.hovered': {
		light: string;
		dark: string;
	};
	'color.background.brand.bold.pressed': {
		light: string;
		dark: string;
	};
	'color.background.brand.boldest': {
		light: string;
		dark: string;
	};
	'color.background.brand.boldest.hovered': {
		light: string;
		dark: string;
	};
	'color.background.brand.boldest.pressed': {
		light: string;
		dark: string;
	};
	'color.background.danger': {
		light: string;
		dark: string;
	};
	'color.background.danger.hovered': {
		light: string;
		dark: string;
	};
	'color.background.danger.pressed': {
		light: string;
		dark: string;
	};
	'color.background.danger.subtler': {
		light: string;
		dark: string;
	};
	'color.background.danger.subtler.hovered': {
		light: string;
		dark: string;
	};
	'color.background.danger.subtler.pressed': {
		light: string;
		dark: string;
	};
	'color.background.danger.bold': {
		light: string;
		dark: string;
	};
	'color.background.danger.bold.hovered': {
		light: string;
		dark: string;
	};
	'color.background.danger.bold.pressed': {
		light: string;
		dark: string;
	};
	'color.background.warning': {
		light: string;
		dark: string;
	};
	'color.background.warning.hovered': {
		light: string;
		dark: string;
	};
	'color.background.warning.pressed': {
		light: string;
		dark: string;
	};
	'color.background.warning.subtler': {
		light: string;
		dark: string;
	};
	'color.background.warning.subtler.hovered': {
		light: string;
		dark: string;
	};
	'color.background.warning.subtler.pressed': {
		light: string;
		dark: string;
	};
	'color.background.warning.bold': {
		light: string;
		dark: string;
	};
	'color.background.warning.bold.hovered': {
		light: string;
		dark: string;
	};
	'color.background.warning.bold.pressed': {
		light: string;
		dark: string;
	};
	'color.background.success': {
		light: string;
		dark: string;
	};
	'color.background.success.hovered': {
		light: string;
		dark: string;
	};
	'color.background.success.pressed': {
		light: string;
		dark: string;
	};
	'color.background.success.subtler': {
		light: string;
		dark: string;
	};
	'color.background.success.subtler.hovered': {
		light: string;
		dark: string;
	};
	'color.background.success.subtler.pressed': {
		light: string;
		dark: string;
	};
	'color.background.success.bold': {
		light: string;
		dark: string;
	};
	'color.background.success.bold.hovered': {
		light: string;
		dark: string;
	};
	'color.background.success.bold.pressed': {
		light: string;
		dark: string;
	};
	'color.background.discovery': {
		light: string;
		dark: string;
	};
	'color.background.discovery.hovered': {
		light: string;
		dark: string;
	};
	'color.background.discovery.pressed': {
		light: string;
		dark: string;
	};
	'color.background.discovery.subtler': {
		light: string;
		dark: string;
	};
	'color.background.discovery.subtler.hovered': {
		light: string;
		dark: string;
	};
	'color.background.discovery.subtler.pressed': {
		light: string;
		dark: string;
	};
	'color.background.discovery.bold': {
		light: string;
		dark: string;
	};
	'color.background.discovery.bold.hovered': {
		light: string;
		dark: string;
	};
	'color.background.discovery.bold.pressed': {
		light: string;
		dark: string;
	};
	'color.background.information': {
		light: string;
		dark: string;
	};
	'color.background.information.hovered': {
		light: string;
		dark: string;
	};
	'color.background.information.pressed': {
		light: string;
		dark: string;
	};
	'color.background.information.subtler': {
		light: string;
		dark: string;
	};
	'color.background.information.subtler.hovered': {
		light: string;
		dark: string;
	};
	'color.background.information.subtler.pressed': {
		light: string;
		dark: string;
	};
	'color.background.information.bold': {
		light: string;
		dark: string;
	};
	'color.background.information.bold.hovered': {
		light: string;
		dark: string;
	};
	'color.background.information.bold.pressed': {
		light: string;
		dark: string;
	};
	'color.blanket': {
		light: string;
		dark: string;
	};
	'color.blanket.selected': {
		light: string;
		dark: string;
	};
	'color.blanket.danger': {
		light: string;
		dark: string;
	};
	'color.interaction.hovered': {
		light: string;
		dark: string;
	};
	'color.interaction.pressed': {
		light: string;
		dark: string;
	};
	'color.skeleton': {
		light: string;
		dark: string;
	};
	'color.skeleton.subtle': {
		light: string;
		dark: string;
	};
	'color.chart.categorical.1': {
		light: string;
		dark: string;
	};
	'color.chart.categorical.1.hovered': {
		light: string;
		dark: string;
	};
	'color.chart.categorical.2': {
		light: string;
		dark: string;
	};
	'color.chart.categorical.2.hovered': {
		light: string;
		dark: string;
	};
	'color.chart.categorical.3': {
		light: string;
		dark: string;
	};
	'color.chart.categorical.3.hovered': {
		light: string;
		dark: string;
	};
	'color.chart.categorical.4': {
		light: string;
		dark: string;
	};
	'color.chart.categorical.4.hovered': {
		light: string;
		dark: string;
	};
	'color.chart.categorical.5': {
		light: string;
		dark: string;
	};
	'color.chart.categorical.5.hovered': {
		light: string;
		dark: string;
	};
	'color.chart.categorical.6': {
		light: string;
		dark: string;
	};
	'color.chart.categorical.6.hovered': {
		light: string;
		dark: string;
	};
	'color.chart.categorical.7': {
		light: string;
		dark: string;
	};
	'color.chart.categorical.7.hovered': {
		light: string;
		dark: string;
	};
	'color.chart.categorical.8': {
		light: string;
		dark: string;
	};
	'color.chart.categorical.8.hovered': {
		light: string;
		dark: string;
	};
	'color.chart.lime.bold': {
		light: string;
		dark: string;
	};
	'color.chart.lime.bold.hovered': {
		light: string;
		dark: string;
	};
	'color.chart.lime.bolder': {
		light: string;
		dark: string;
	};
	'color.chart.lime.bolder.hovered': {
		light: string;
		dark: string;
	};
	'color.chart.lime.boldest': {
		light: string;
		dark: string;
	};
	'color.chart.lime.boldest.hovered': {
		light: string;
		dark: string;
	};
	'color.chart.neutral': {
		light: string;
		dark: string;
	};
	'color.chart.neutral.hovered': {
		light: string;
		dark: string;
	};
	'color.chart.red.bold': {
		light: string;
		dark: string;
	};
	'color.chart.red.bold.hovered': {
		light: string;
		dark: string;
	};
	'color.chart.red.bolder': {
		light: string;
		dark: string;
	};
	'color.chart.red.bolder.hovered': {
		light: string;
		dark: string;
	};
	'color.chart.red.boldest': {
		light: string;
		dark: string;
	};
	'color.chart.red.boldest.hovered': {
		light: string;
		dark: string;
	};
	'color.chart.orange.bold': {
		light: string;
		dark: string;
	};
	'color.chart.orange.bold.hovered': {
		light: string;
		dark: string;
	};
	'color.chart.orange.bolder': {
		light: string;
		dark: string;
	};
	'color.chart.orange.bolder.hovered': {
		light: string;
		dark: string;
	};
	'color.chart.orange.boldest': {
		light: string;
		dark: string;
	};
	'color.chart.orange.boldest.hovered': {
		light: string;
		dark: string;
	};
	'color.chart.yellow.bold': {
		light: string;
		dark: string;
	};
	'color.chart.yellow.bold.hovered': {
		light: string;
		dark: string;
	};
	'color.chart.yellow.bolder': {
		light: string;
		dark: string;
	};
	'color.chart.yellow.bolder.hovered': {
		light: string;
		dark: string;
	};
	'color.chart.yellow.boldest': {
		light: string;
		dark: string;
	};
	'color.chart.yellow.boldest.hovered': {
		light: string;
		dark: string;
	};
	'color.chart.green.bold': {
		light: string;
		dark: string;
	};
	'color.chart.green.bold.hovered': {
		light: string;
		dark: string;
	};
	'color.chart.green.bolder': {
		light: string;
		dark: string;
	};
	'color.chart.green.bolder.hovered': {
		light: string;
		dark: string;
	};
	'color.chart.green.boldest': {
		light: string;
		dark: string;
	};
	'color.chart.green.boldest.hovered': {
		light: string;
		dark: string;
	};
	'color.chart.teal.bold': {
		light: string;
		dark: string;
	};
	'color.chart.teal.bold.hovered': {
		light: string;
		dark: string;
	};
	'color.chart.teal.bolder': {
		light: string;
		dark: string;
	};
	'color.chart.teal.bolder.hovered': {
		light: string;
		dark: string;
	};
	'color.chart.teal.boldest': {
		light: string;
		dark: string;
	};
	'color.chart.teal.boldest.hovered': {
		light: string;
		dark: string;
	};
	'color.chart.blue.bold': {
		light: string;
		dark: string;
	};
	'color.chart.blue.bold.hovered': {
		light: string;
		dark: string;
	};
	'color.chart.blue.bolder': {
		light: string;
		dark: string;
	};
	'color.chart.blue.bolder.hovered': {
		light: string;
		dark: string;
	};
	'color.chart.blue.boldest': {
		light: string;
		dark: string;
	};
	'color.chart.blue.boldest.hovered': {
		light: string;
		dark: string;
	};
	'color.chart.purple.bold': {
		light: string;
		dark: string;
	};
	'color.chart.purple.bold.hovered': {
		light: string;
		dark: string;
	};
	'color.chart.purple.bolder': {
		light: string;
		dark: string;
	};
	'color.chart.purple.bolder.hovered': {
		light: string;
		dark: string;
	};
	'color.chart.purple.boldest': {
		light: string;
		dark: string;
	};
	'color.chart.purple.boldest.hovered': {
		light: string;
		dark: string;
	};
	'color.chart.magenta.bold': {
		light: string;
		dark: string;
	};
	'color.chart.magenta.bold.hovered': {
		light: string;
		dark: string;
	};
	'color.chart.magenta.bolder': {
		light: string;
		dark: string;
	};
	'color.chart.magenta.bolder.hovered': {
		light: string;
		dark: string;
	};
	'color.chart.magenta.boldest': {
		light: string;
		dark: string;
	};
	'color.chart.magenta.boldest.hovered': {
		light: string;
		dark: string;
	};
	'color.chart.gray.bold': {
		light: string;
		dark: string;
	};
	'color.chart.gray.bold.hovered': {
		light: string;
		dark: string;
	};
	'color.chart.gray.bolder': {
		light: string;
		dark: string;
	};
	'color.chart.gray.bolder.hovered': {
		light: string;
		dark: string;
	};
	'color.chart.gray.boldest': {
		light: string;
		dark: string;
	};
	'color.chart.gray.boldest.hovered': {
		light: string;
		dark: string;
	};
	'color.chart.brand': {
		light: string;
		dark: string;
	};
	'color.chart.brand.hovered': {
		light: string;
		dark: string;
	};
	'color.chart.danger': {
		light: string;
		dark: string;
	};
	'color.chart.danger.hovered': {
		light: string;
		dark: string;
	};
	'color.chart.danger.bold': {
		light: string;
		dark: string;
	};
	'color.chart.danger.bold.hovered': {
		light: string;
		dark: string;
	};
	'color.chart.warning': {
		light: string;
		dark: string;
	};
	'color.chart.warning.hovered': {
		light: string;
		dark: string;
	};
	'color.chart.warning.bold': {
		light: string;
		dark: string;
	};
	'color.chart.warning.bold.hovered': {
		light: string;
		dark: string;
	};
	'color.chart.success': {
		light: string;
		dark: string;
	};
	'color.chart.success.hovered': {
		light: string;
		dark: string;
	};
	'color.chart.success.bold': {
		light: string;
		dark: string;
	};
	'color.chart.success.bold.hovered': {
		light: string;
		dark: string;
	};
	'color.chart.discovery': {
		light: string;
		dark: string;
	};
	'color.chart.discovery.hovered': {
		light: string;
		dark: string;
	};
	'color.chart.discovery.bold': {
		light: string;
		dark: string;
	};
	'color.chart.discovery.bold.hovered': {
		light: string;
		dark: string;
	};
	'color.chart.information': {
		light: string;
		dark: string;
	};
	'color.chart.information.hovered': {
		light: string;
		dark: string;
	};
	'color.chart.information.bold': {
		light: string;
		dark: string;
	};
	'color.chart.information.bold.hovered': {
		light: string;
		dark: string;
	};
	'elevation.surface': {
		light: string;
		dark: string;
	};
	'elevation.surface.hovered': {
		light: string;
		dark: string;
	};
	'elevation.surface.pressed': {
		light: string;
		dark: string;
	};
	'elevation.surface.overlay': {
		light: string;
		dark: string;
	};
	'elevation.surface.overlay.hovered': {
		light: string;
		dark: string;
	};
	'elevation.surface.overlay.pressed': {
		light: string;
		dark: string;
	};
	'elevation.surface.raised': {
		light: string;
		dark: string;
	};
	'elevation.surface.raised.hovered': {
		light: string;
		dark: string;
	};
	'elevation.surface.raised.pressed': {
		light: string;
		dark: string;
	};
	'elevation.surface.sunken': {
		light: string;
		dark: string;
	};
	'elevation.shadow.overflow': {
		light: string;
		dark: string;
	};
	'elevation.shadow.overflow.perimeter': {
		light: string;
		dark: string;
	};
	'elevation.shadow.overflow.spread': {
		light: string;
		dark: string;
	};
	'elevation.shadow.overlay': {
		light: string;
		dark: string;
	};
	'elevation.shadow.raised': {
		light: string;
		dark: string;
	};
	'opacity.disabled': {
		light: string;
		dark: string;
	};
	'opacity.loading': {
		light: string;
		dark: string;
	};
	'utility.UNSAFE.transparent': {
		light: string;
		dark: string;
	};
	'utility.elevation.surface.current': {
		light: string;
		dark: string;
	};
	'space.0': {
		light: string;
		dark: string;
	};
	'space.025': {
		light: string;
		dark: string;
	};
	'space.050': {
		light: string;
		dark: string;
	};
	'space.075': {
		light: string;
		dark: string;
	};
	'space.100': {
		light: string;
		dark: string;
	};
	'space.150': {
		light: string;
		dark: string;
	};
	'space.200': {
		light: string;
		dark: string;
	};
	'space.250': {
		light: string;
		dark: string;
	};
	'space.300': {
		light: string;
		dark: string;
	};
	'space.400': {
		light: string;
		dark: string;
	};
	'space.500': {
		light: string;
		dark: string;
	};
	'space.600': {
		light: string;
		dark: string;
	};
	'space.800': {
		light: string;
		dark: string;
	};
	'space.1000': {
		light: string;
		dark: string;
	};
	'space.negative.025': {
		light: string;
		dark: string;
	};
	'space.negative.050': {
		light: string;
		dark: string;
	};
	'space.negative.075': {
		light: string;
		dark: string;
	};
	'space.negative.100': {
		light: string;
		dark: string;
	};
	'space.negative.150': {
		light: string;
		dark: string;
	};
	'space.negative.200': {
		light: string;
		dark: string;
	};
	'space.negative.250': {
		light: string;
		dark: string;
	};
	'space.negative.300': {
		light: string;
		dark: string;
	};
	'space.negative.400': {
		light: string;
		dark: string;
	};
	'font.heading.xxlarge': {
		light: string;
		dark: string;
	};
	'font.heading.xlarge': {
		light: string;
		dark: string;
	};
	'font.heading.large': {
		light: string;
		dark: string;
	};
	'font.heading.medium': {
		light: string;
		dark: string;
	};
	'font.heading.small': {
		light: string;
		dark: string;
	};
	'font.heading.xsmall': {
		light: string;
		dark: string;
	};
	'font.heading.xxsmall': {
		light: string;
		dark: string;
	};
	'font.body.large': {
		light: string;
		dark: string;
	};
	'font.body': {
		light: string;
		dark: string;
	};
	'font.body.small': {
		light: string;
		dark: string;
	};
	'font.metric.large': {
		light: string;
		dark: string;
	};
	'font.metric.medium': {
		light: string;
		dark: string;
	};
	'font.metric.small': {
		light: string;
		dark: string;
	};
	'font.code': {
		light: string;
		dark: string;
	};
	'font.weight.regular': {
		light: string;
		dark: string;
	};
	'font.weight.medium': {
		light: string;
		dark: string;
	};
	'font.weight.semibold': {
		light: string;
		dark: string;
	};
	'font.weight.bold': {
		light: string;
		dark: string;
	};
	'font.family.heading': {
		light: string;
		dark: string;
	};
	'font.family.body': {
		light: string;
		dark: string;
	};
	'font.family.code': {
		light: string;
		dark: string;
	};
	'font.family.brand.heading': {
		light: string;
		dark: string;
	};
	'font.family.brand.body': {
		light: string;
		dark: string;
	};
	'radius.xsmall': {
		light: string;
		dark: string;
	};
	'radius.small': {
		light: string;
		dark: string;
	};
	'radius.medium': {
		light: string;
		dark: string;
	};
	'radius.large': {
		light: string;
		dark: string;
	};
	'radius.xlarge': {
		light: string;
		dark: string;
	};
	'radius.xxlarge': {
		light: string;
		dark: string;
	};
	'radius.full': {
		light: string;
		dark: string;
	};
	'radius.tile': {
		light: string;
		dark: string;
	};
	'border.width': {
		light: string;
		dark: string;
	};
	'border.width.selected': {
		light: string;
		dark: string;
	};
	'border.width.focused': {
		light: string;
		dark: string;
	};
} = mergedTokens
	.filter(
		(t): t is ColorModeTokenMerged =>
			!!t.cleanName &&
			!!t.darkToken?.value &&
			t.darkToken.value !== t.value &&
			tokenNames.includes(t.cleanName),
	)
	.reduce(
		(a, t) =>
			Object.assign(a, {
				[t.cleanName]: { light: t.value, dark: t.darkToken.value },
			}),
		{} as { [K in TokenNames]: { light: string; dark: string } },
	);
